import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useMemo, useRef, useState } from 'react'

import {
  type OrderDraftItemMutationStatusEnum,
  OrderDraftItemMutationStatuses,
} from './OrderDraftItemMutationStatusEnum'
import { checkIsFailureMutationStatusUseCase } from '../../business-logic/use-cases/CheckIsFailureMutationStatusUseCase'

export type OrderDraftItemMutationVisibleStatus = Exclude<
  OrderDraftItemMutationStatusEnum,
  typeof OrderDraftItemMutationStatuses.COMPLETED
>

interface OrderDraftItemMutationEntry {
  status: OrderDraftItemMutationVisibleStatus
  inflightCount: number
  latestMutationSeq: number
}

type OrderDraftItemMutationStatusMap = Record<
  number,
  OrderDraftItemMutationEntry | undefined
>

interface IOrderDraftItemMutationStatusContextValue {
  getItemMutationStatus: (
    itemId: number
  ) => OrderDraftItemMutationVisibleStatus | null
  setItemMutationStatus: (
    itemId: number,
    status: OrderDraftItemMutationStatusEnum
  ) => number | null
  isLatestMutation: (itemId: number, seq: number) => boolean
}

export const OrderDraftItemMutationStatusContext =
  createContext<IOrderDraftItemMutationStatusContextValue | null>(null)

export const OrderDraftItemMutationStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [mutationStatuses, setMutationStatuses] =
    useState<OrderDraftItemMutationStatusMap>({})

  // Monotonically increasing counter for mutation ordering.
  // Lives in a ref so setItemMutationStatus can return the
  // assigned sequence synchronously.
  const seqCounterRef = useRef(0)

  const getItemMutationStatus = useCallback(
    (itemId: number): OrderDraftItemMutationVisibleStatus | null => {
      return mutationStatuses[itemId]?.status ?? null
    },
    [mutationStatuses]
  )

  const isLatestMutation = useCallback(
    (itemId: number, seq: number): boolean => {
      const entry = mutationStatuses[itemId]
      if (!entry) return false
      return seq >= entry.latestMutationSeq
    },
    [mutationStatuses]
  )

  const setItemMutationStatus = useCallback(
    (
      itemId: number,
      status: OrderDraftItemMutationStatusEnum
    ): number | null => {
      // Only calculate a new sequence number for statuses that represent a new mutation. Otherwise, return null.
      const isStatusForNewMutation =
        !checkIsFailureMutationStatusUseCase(status) &&
        status !== OrderDraftItemMutationStatuses.COMPLETED
      const seq = isStatusForNewMutation ? ++seqCounterRef.current : null

      setMutationStatuses((prev) => {
        const current = prev[itemId]
        const currentCount = current?.inflightCount ?? 0

        if (status === OrderDraftItemMutationStatuses.COMPLETED) {
          if (!current) return prev

          if (currentCount <= 1) {
            const { [itemId]: _, ...rest } = prev
            return rest
          }

          // Other requests still in-flight — keep current status
          return {
            ...prev,
            [itemId]: { ...current, inflightCount: currentCount - 1 },
          }
        }

        if (checkIsFailureMutationStatusUseCase(status)) {
          return {
            ...prev,
            [itemId]: {
              status,
              inflightCount: Math.max(currentCount - 1, 0),
              latestMutationSeq: current?.latestMutationSeq ?? 0, // This should never default to 0, but we'll add a safety check just in case. A value of 0 is safe as any active mutation will always have a higher sequence number.
            },
          }
        }

        // This should never happen, but we'll add a safety check just in case.
        if (typeof seq !== 'number') {
          console.error('Unexpected sequence value:', seq)
          return prev
        }

        // In-progress status — increment count, update seq
        return {
          ...prev,
          [itemId]: {
            status,
            inflightCount: currentCount + 1,
            latestMutationSeq: seq,
          },
        }
      })

      return seq
    },
    []
  )

  const contextValue = useMemo(
    () => ({
      getItemMutationStatus,
      setItemMutationStatus,
      isLatestMutation,
      // hasUnsavedChanges, // TODO (PUR-3406): function for checking if any item has inflight or failed statuses indicating there are unsaved changes.
    }),
    [getItemMutationStatus, setItemMutationStatus, isLatestMutation]
  )

  return (
    <OrderDraftItemMutationStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftItemMutationStatusContext.Provider>
  )
}
